'use client';

import { useTranslation } from 'next-i18next';
import {
  useEffect, useRef, useState, useCallback,
  useMemo,
} from 'react';
import { Button, bjToast, SanitizeHTML } from '@components/common';
import { generateUuid } from '@utils/index';

import Webcam from 'react-webcam';
import { uploadResumeVideo } from '@services/firebase/uploadResumeVideo';
import { useAppSelector } from '@store/index';
import { useSwrTextToSpeech } from '@hooks/useSwrApi/endpoints/text-to-speech/useSwrTextToSpeech';
import { uploadJobInterviewVideo } from '@root/src/services/firebase/uploadJobInterviewVideo';
import sendGoogleTagEvent from '@root/src/hooks/useSendGoogleTagEvent';
import { InterviewerBia } from './InterviewerBia';
import { WebcamPreview } from './WebcamPreview';
import { usePCMPlayer } from './utils/usePCMPlayer';

interface TimersConfiguration {
  readCountdown: number; // Countdown before recording starts
  recordingDuration: number; // Duration of the recording
  timeBeforeManualStartEnabled: number; // Time before user can start recording without waiting for reading countdown
  timeBeforeManualNextEnabled: number; // Time before user can skip to next question
}

// Define interview question type
interface InterviewQuestion {
  type: string;
  question: string;
}

// Define interview recording type
interface InterviewRecording {
  questionId?: number;
  questionText: string;
  questionType: string;
  videoUrl?: string;
  videoFile?: File;
  videoId?: string;
  fileName?: string;
  timestamp: number;
  duration?: number;
  isUploaded?: boolean;
  isUploading?: boolean;
}

interface AIInterviewProps {
  locale: Locale;
  questionsFromApi: InterviewQuestion[] | ApiSchema<'InterviewUserQuestionRead'>[] | null;
  autostart?: boolean;
  fileNamePrefix?: string; // Optional prefix for the file name
  firebaseFolder?: string; // Optional Firebase folder for uploads
  endScreen?: React.ReactNode; // Optional custom end screen
  timersConfiguration?: TimersConfiguration; // Optional timers configuration
  onComplete?: () => void; // Optional callback when interview completes
}

const TOTAL_QUESTIONS = 3;

const AIInterviewFlow = (props: AIInterviewProps) => {
  const {
    locale,
    questionsFromApi,
    autostart,
    fileNamePrefix = 'iVideo',
    firebaseFolder = 'resume_interview_video',
    endScreen,
    timersConfiguration = {
      readCountdown: 10,
      recordingDuration: 30,
      timeBeforeManualStartEnabled: 0,
      timeBeforeManualNextEnabled: 10,
    },
    onComplete,
  } = props;
  const isSafari = typeof window !== 'undefined' && /^((?!chrome|android).)*safari/i.test(window.navigator.userAgent);
  const { t } = useTranslation('common');

  const headerInfo = useAppSelector((state) => state.user.headerInfo);
  const userId = headerInfo?.userId;

  const {
    data: ttsAudioStream, trigger: getTTS, reset: resetTTS,
  } = useSwrTextToSpeech({
    locale,
  });
  const { play: pcmPlay, stop: pcmStop, isPlaying } = usePCMPlayer({
    onStop: resetTTS,
  });

  const playTTS = useCallback(() => {
    if (!ttsAudioStream) return;

    if (ttsAudioStream instanceof Blob) {
      void ttsAudioStream.arrayBuffer().then((buffer) => {
        pcmPlay(buffer);
      });
    } else {
      pcmPlay(ttsAudioStream as ArrayBuffer);
    }
  }, [pcmPlay, ttsAudioStream]);

  // Permission and media states
  const [microphoneCameraPermission, setMicrophoneCameraPermission] = useState<boolean>(false);
  const [hasRejectedPermission, setHasRejectedPermission] = useState<boolean>(false);

  // Recording states
  const webcamRef = useRef<Webcam>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const [recordingStatus, setRecordingStatus] = useState<'recording' | 'inactive'>('inactive');

  // Flag to prevent multiple calls to nextQuestion
  const isTransitioningRef = useRef(false);

  // Interview flow states
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [isInterviewComplete, setIsInterviewComplete] = useState<boolean>(false);

  // Timer state
  const [timeLeft, setTimeLeft] = useState<number>(timersConfiguration.recordingDuration); // Recording duration in seconds

  // Automatic start with countdown
  const [automaticStart] = useState<boolean>(true); // Set to true to enable automatic start
  const [countdown, setCountdown] = useState<number | null>(null); // Countdown before recording starts

  // Recordings state
  const [recordings, setRecordings] = useState<InterviewRecording[]>([]);

  // File input ref for FirebaseResumeVideoUpload
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [hasConfirmedUpload, setHasConfirmedUpload] = useState<boolean>(false);
  const [currentUploadIndex, setCurrentUploadIndex] = useState<number>(-1);

  // Hardcoded questions (to be replaced with dynamic ones later)
  const [questions, setQuestions] = useState<InterviewQuestion[] | ApiSchema<'InterviewUserQuestionRead'>[]>([]);

  // Ref for the audio element used for TTS
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (ttsAudioStream && countdown && !isPlaying) {
      playTTS();
    }
  }, [ttsAudioStream, countdown, isPlaying, playTTS]);

  // Check if webcam permissions are granted
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });

        // Stop the stream immediately after checking permissions
        stream.getTracks().forEach((track) => {
          track.stop();
        });

        setMicrophoneCameraPermission(true);
      } catch (error) {
        console.error('Error getting camera permission:', error);
        setMicrophoneCameraPermission(false);
        setHasRejectedPermission(true);
        bjToast.error(t('video.cv.error.camera_permission'));
      }
    };

    void checkPermissions();
  }, [t]);

  // Handle recording with react-webcam
  const handleStartRecording = useCallback(() => {
    pcmStop(); // Stop any ongoing text-to-speech before starting recording
    setRecordingStatus('recording');
    setCountdown(null);
    setTimeLeft(timersConfiguration.recordingDuration);
    // Preload TTS for next question while recording current one
    const nextQuestion = questions[currentQuestionIndex + 1];
    if (
      nextQuestion && typeof nextQuestion === 'object' && (!('audioUrl' in nextQuestion) || !nextQuestion.audioUrl)
    ) {
      void getTTS({ text: nextQuestion.question || '', language: locale });
    }

    const webcam = webcamRef.current;
    if (!webcam || !webcam.stream) return;

    // Enable audio tracks for recording
    webcam.stream.getAudioTracks().forEach((track) => {
      track.enabled = true;
    });

    const mimeType = isSafari ? 'video/mp4' : 'video/webm';
    const mediaRecorder = new MediaRecorder(webcam.stream, {
      mimeType,
    });

    mediaRecorderRef.current = mediaRecorder;

    // Create a local array to collect chunks
    const localChunks: Blob[] = [];

    mediaRecorder.addEventListener('dataavailable', ({ data }) => {
      if (data.size > 0) {
        // Add to local array
        localChunks.push(data);
      }
    });

    mediaRecorder.addEventListener('stop', () => {
      // Process the collected chunks
      if (localChunks.length > 0) {
        const blob = new Blob(localChunks, {
          type: mimeType,
        });

        const url = URL.createObjectURL(blob);

        // Generate a unique ID for this recording
        const fileId = generateUuid();

        // Create a File object from the Blob
        const fileName = `${fileNamePrefix}_${userId}_${fileId}.${isSafari ? 'mp4' : 'webm'}`;
        const file = new File([blob], fileName, { type: mimeType });

        const currentQuestion = questions[currentQuestionIndex];
        const questionType = typeof currentQuestion === 'object' && 'type' in currentQuestion
          ? currentQuestion.type || ''
          : '';
        const newRecording: InterviewRecording = {
          questionId: typeof currentQuestion === 'object' && 'questionId' in currentQuestion ? currentQuestion.questionId : undefined,
          questionText: currentQuestion.question || '',
          questionType,
          videoUrl: url,
          videoFile: file,
          videoId: fileId,
          fileName,
          timestamp: Date.now(),
          duration: timersConfiguration.recordingDuration - timeLeft, // Calculate duration based on time left
          isUploading: true, // Mark as currently uploading
          isUploaded: false,
        };

        // Add the new recording to the recordings array first, then set up for upload
        if (fileInputRef.current) {
          const dataTransfer = new DataTransfer();
          dataTransfer.items.add(file);
          fileInputRef.current.files = dataTransfer.files;

          // Add the new recording to the recordings array
          setRecordings((prev) => {
            const newRecordings = [...prev, newRecording];

            // Set the current upload index to the last recording (the one we just added)
            setCurrentUploadIndex(newRecordings.length - 1);

            // Trigger the upload
            setHasConfirmedUpload(true);

            return newRecordings;
          });
        } else {
          // If fileInputRef is not available, just add the recording without uploading
          setRecordings((prev) => [...prev, newRecording]);
        }
      }
    });

    mediaRecorder.start();
  }, [currentQuestionIndex, isSafari, questions, timeLeft, userId, pcmStop, getTTS, locale, fileNamePrefix, timersConfiguration.recordingDuration]);

  const handleStopRecording = useCallback(() => {
    setRecordingStatus('inactive');

    const mediaRecorder = mediaRecorderRef.current;
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }
  }, []);

  // Move to the next question
  const nextQuestion = useCallback(() => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);

      // Reset recording status to inactive for the next question
      setRecordingStatus('inactive');
      // The useEffect will handle automatic start if enabled
    } else {
      // Interview is complete
      // Call onComplete callback if provided
      if (onComplete) {
        onComplete();
      }

      setIsInterviewComplete(true);
      setQuestions([]); // Clear questions to prepare for next interview
    }
  }, [currentQuestionIndex, questions.length, onComplete]);

  // Preload TTS for the first question when questions are loaded
  useEffect(() => {
    const firstQuestion = questionsFromApi?.[0];
    if (firstQuestion && typeof firstQuestion === 'object' && (!('audioUrl' in firstQuestion) || !firstQuestion.audioUrl)) {
      void getTTS({ text: questionsFromApi[0].question || '', language: locale });
    }
  }, [questionsFromApi, getTTS, locale]);

  // Handle manual skip to next question
  const handleNextQuestion = useCallback(() => {
    if (recordingStatus === 'recording') {
      handleStopRecording();
      // Only proceed if we're not already transitioning to the next question
      if (!isTransitioningRef.current) {
        isTransitioningRef.current = true;
        setTimeout(() => {
          nextQuestion();
          // Reset the flag after transitioning
          isTransitioningRef.current = false;
        }, 1000); // Delay to ensure recording is properly stopped and processed
      }
    } else {
      // If not recording, move to next question immediately
      nextQuestion();
    }
  }, [handleStopRecording, nextQuestion, recordingStatus]);

  // Handle upload completion
  const handleUploadComplete = useCallback((success: boolean) => {
    if (success) {
      // Update the recording status to mark it as uploaded
      setRecordings((prev) => {
        const updatedRecordings = [...prev];
        if (currentUploadIndex >= 0 && currentUploadIndex < updatedRecordings.length) {
          updatedRecordings[currentUploadIndex] = {
            ...updatedRecordings[currentUploadIndex],
            isUploading: false,
            isUploaded: true,
          };
        }
        return updatedRecordings;
      });
      sendGoogleTagEvent('interview_answer', 'Custom');

      // Reset for the next upload
      setHasConfirmedUpload(false);
      setCurrentUploadIndex(-1);
    } else {
      // Mark the recording as failed
      setRecordings((prev) => {
        const updatedRecordings = [...prev];
        if (currentUploadIndex >= 0 && currentUploadIndex < updatedRecordings.length) {
          updatedRecordings[currentUploadIndex] = {
            ...updatedRecordings[currentUploadIndex],
            isUploading: false,
            isUploaded: false,
          };
        }
        return updatedRecordings;
      });

      bjToast.error(t('interview.recording.upload.error'));
      setHasConfirmedUpload(false);
      setCurrentUploadIndex(-1);
    }
  }, [currentUploadIndex, t]);

  // Set up file for upload when currentUploadIndex changes
  useEffect(() => {
    if (currentUploadIndex >= 0 && currentUploadIndex < recordings.length) {
      const recording = recordings[currentUploadIndex];
      if (recording.videoFile && hasConfirmedUpload) {
        // Call the uploadResumeVideo function directly with the stored question
        if (recording.questionId) {
          void uploadJobInterviewVideo(
            recording.videoFile,
            locale,
            recording.questionId,
            firebaseFolder,
            handleUploadComplete,
          );
        } else {
          void uploadResumeVideo(
            recording.videoFile,
            locale,
            recording.questionText,
            recording.questionType === 'start',
            firebaseFolder,
            handleUploadComplete,
          );
        }

        // Reset the hasConfirmedUpload flag after initiating the upload
        setHasConfirmedUpload(false);
      }
    }
  }, [currentUploadIndex, recordings, hasConfirmedUpload, locale, handleUploadComplete, firebaseFolder]);

  // Timer effect for countdown during recording
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (recordingStatus === 'recording') {
      interval = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            handleStopRecording();
            // Don't immediately call nextQuestion() - wait for recording to stop
            // Only proceed if we're not already transitioning to the next question
            if (!isTransitioningRef.current) {
              isTransitioningRef.current = true;
              setTimeout(() => {
                nextQuestion();
                // Reset the flag after transitioning
                isTransitioningRef.current = false;
              }, 1000); // Increased delay to ensure recording is properly stopped and processed
            }
            return timersConfiguration.recordingDuration;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      clearInterval(interval);
    };
  }, [recordingStatus, handleStopRecording, nextQuestion, timersConfiguration]);

  // Start countdown when a new question is loaded and automaticStart is enabled
  useEffect(() => {
    if (automaticStart && questions.length > 0 && recordingStatus === 'inactive' && !isInterviewComplete && microphoneCameraPermission && userId) {
      // Start reading countdown
      setCountdown(timersConfiguration.readCountdown);

      const countdownInterval = setInterval(() => {
        setCountdown((prev) => {
          if (prev === null || prev <= 1) {
            clearInterval(countdownInterval);
            // Start recording when countdown reaches 0
            handleStartRecording();
            return null;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        clearInterval(countdownInterval);
      };
    }

    return undefined;
  }, [
    automaticStart, currentQuestionIndex,
    handleStartRecording, isInterviewComplete, questions,
    recordingStatus, microphoneCameraPermission, userId, timersConfiguration.readCountdown,
  ]);

  const startInterview = () => {
    if (questionsFromApi && questionsFromApi?.length > 0) {
      setQuestions(questionsFromApi);
    }
  };

  if (autostart && questionsFromApi && questionsFromApi?.length > 0 && questions.length === 0) {
    setQuestions(questionsFromApi);
  }

  // Compute the index of the question being displayed to show it as "2/3".
  // This will work even when user has a partial interview and comes at a later point to finish it.
  const questionDisplayedIndex = useMemo(
    () => TOTAL_QUESTIONS - questions.length + currentQuestionIndex + 1,
    [currentQuestionIndex, questions],
  );

  // Cleanup effect to stop audio on unmount or question change
  useEffect(() => () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [currentQuestionIndex]);

  return (
    <>
      {isInterviewComplete ? (
        endScreen
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* title and description, questions */}
          <div className="order-1 flex flex-col lg:justify-end">
            {questions?.length === 0 && !autostart && (
              <>
                <h1 className="text-xl font-bold">
                  {t('ai.interview.title')}
                </h1>
                <SanitizeHTML
                  as="p"
                  htmlContent={t('ai.interview.description')}
                  className="mt-5"
                />
              </>
            )}
            {questions?.length > 0 && (
            <>
              <h3 className="mt-5 text-sm text-ink-medium">
                {
                      typeof questions[currentQuestionIndex] === 'object'
                      && 'type' in questions[currentQuestionIndex]
                      && questions[currentQuestionIndex]?.type === 'start'
                        ? t('ai.interview.question.start')
                        : `${t('ai.interview.question.normal')} ${questionDisplayedIndex}/${TOTAL_QUESTIONS}`
                    }
              </h3>
              <p className="mt-5">
                {questions[currentQuestionIndex]?.question}
                {
                  questions[currentQuestionIndex] && typeof questions[currentQuestionIndex] === 'object' && 'audioUrl' in questions[currentQuestionIndex] && questions[currentQuestionIndex].audioUrl && microphoneCameraPermission && (
                    <audio
                      ref={audioRef}
                      autoPlay
                      src={questions[currentQuestionIndex].audioUrl}
                    />
                  )
                }
              </p>
            </>
            )}
          </div>
          {/* webcam preview */}
          <div className="order-2 lg:col-span-2 lg:row-span-2 lg:self-center">
            <WebcamPreview
              hasGivenMediaPermissions={microphoneCameraPermission}
              hasRejectedPermission={hasRejectedPermission}
              timeLeft={recordingStatus === 'recording' ? timeLeft : null}
              countdown={countdown}
              webcamRef={webcamRef}
            />
          </div>
          <div className="order-3">
            <div className="flex items-center justify-between gap-4 pl-2">
              <div>
                {/* Bia Interviewer Avatar - Waiting - Visible before starting interview */}
                {questions.length === 0 && (
                <InterviewerBia description={t('ai.interview.interviewer.waiting')} />
                )}
                {/* Bia Interviewer Avatar - Listening - Visible while recording an answer */}
                {questions.length > 0 && recordingStatus === 'recording' && (
                <InterviewerBia description={t('ai.interview.interviewer.listening')} />
                )}
              </div>

              {/* While recording allow user to skip to next question after timersConfiguration.timeBeforeManualNextEnabled */}
              {recordingStatus === 'recording' && (
              <Button
                color="secondary"
                size="sm"
                onClick={handleNextQuestion}
                rounding="full"
                disabled={timeLeft > timersConfiguration.recordingDuration - timersConfiguration.timeBeforeManualNextEnabled}
              >
                {currentQuestionIndex + 1 === questions.length
                  ? t(
                    'premium.talent.applicationSummary.modal.save.text',
                  )
                  : t('ai.interview.next')}
              </Button>
              )}

              {/* While the countdown is active allow user to start the recording */}
              {recordingStatus === 'inactive'
                && questions.length > 0
                && countdown
                && (
                <Button
                  color="secondary"
                  size="sm"
                  onClick={handleStartRecording}
                  rounding="full"
                  disabled={
                    !!timersConfiguration.timeBeforeManualStartEnabled
                    && countdown > timersConfiguration.readCountdown - timersConfiguration.timeBeforeManualStartEnabled
                  }
                >
                  {t('ai.interview.start.recording')}
                </Button>
                )}

              {/* Start interview button */}
              {questions.length === 0 && (
              <Button
                color="secondary"
                onClick={startInterview}
                disabled={
                        !microphoneCameraPermission
                        || (!!questionsFromApi && questionsFromApi?.length === 0)
                      }
                rounding="full"
              >
                {
                      questionsFromApi && questionsFromApi?.length > 0
                        ? t('ai.interview.banner.cta')
                        : t('ai.interview.already.completed')
                    }
              </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Hidden file input for video uploads */}
      <input type="file" ref={fileInputRef} hidden />
    </>
  );
};

export default AIInterviewFlow;
